import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';

function hexToRgb(hex) {
  const value = String(hex || '#0f766e').replace('#', '');
  const normalized = value.length === 3 ? value.split('').map((part) => part + part).join('') : value;
  const number = Number.parseInt(normalized, 16);

  return rgb(
    ((number >> 16) & 255) / 255,
    ((number >> 8) & 255) / 255,
    (number & 255) / 255
  );
}

function drawWrappedText(page, text, x, y, maxWidth, font, size, color, lineHeight = 14) {
  const words = String(text || '-').split(/\s+/);
  const lines = [];
  let currentLine = '';

  words.forEach((word) => {
    const candidate = currentLine ? `${currentLine} ${word}` : word;
    const width = font.widthOfTextAtSize(candidate, size);

    if (width > maxWidth && currentLine) {
      lines.push(currentLine);
      currentLine = word;
    } else {
      currentLine = candidate;
    }
  });

  if (currentLine) {
    lines.push(currentLine);
  }

  lines.forEach((line, index) => {
    page.drawText(line, {
      x,
      y: y - index * lineHeight,
      size,
      font,
      color,
    });
  });

  return y - Math.max(lines.length - 1, 0) * lineHeight;
}

function drawLabel(page, text, x, y, font, size = 9) {
  page.drawText(text, {
    x,
    y,
    size,
    font,
    color: rgb(0.4, 0.49, 0.6),
  });
}

function money(value, currency) {
  return `${Number(value || 0).toFixed(2)} ${currency || 'EUR'}`;
}

async function embedLogo(pdf, imageData) {
  if (!imageData || typeof imageData !== 'string') {
    return null;
  }

  const bytes = await fetch(imageData).then((response) => response.arrayBuffer());

  if (imageData.startsWith('data:image/png')) {
    return pdf.embedPng(bytes);
  }

  if (imageData.startsWith('data:image/jpeg') || imageData.startsWith('data:image/jpg')) {
    return pdf.embedJpg(bytes);
  }

  return null;
}

function escapeXml(value) {
  return String(value || '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&apos;');
}

export function buildCiiXml(invoice) {
  const issueDate = String(invoice.issueDate || '').replaceAll('-', '');
  const dueDate = String(invoice.dueDate || '').replaceAll('-', '');
  const serviceDate = String(invoice.serviceDate || '').replaceAll('-', '');
  const lines = (invoice.lines || []).map((line, index) => {
    const quantity = Number(line.quantity) || 1;
    const unitAmount = Number(line.unitAmount) || 0;
    const lineAmount = Number(line.lineAmount) || quantity * unitAmount;

    return `
      <ram:IncludedSupplyChainTradeLineItem>
        <ram:AssociatedDocumentLineDocument>
          <ram:LineID>${index + 1}</ram:LineID>
        </ram:AssociatedDocumentLineDocument>
        <ram:SpecifiedTradeProduct>
          <ram:Name>${escapeXml(line.description || 'Prestation')}</ram:Name>
        </ram:SpecifiedTradeProduct>
        <ram:SpecifiedLineTradeAgreement>
          <ram:NetPriceProductTradePrice>
            <ram:ChargeAmount>${unitAmount.toFixed(2)}</ram:ChargeAmount>
          </ram:NetPriceProductTradePrice>
        </ram:SpecifiedLineTradeAgreement>
        <ram:SpecifiedLineTradeDelivery>
          <ram:BilledQuantity unitCode="H87">${quantity}</ram:BilledQuantity>
        </ram:SpecifiedLineTradeDelivery>
        <ram:SpecifiedLineTradeSettlement>
          <ram:ApplicableTradeTax>
            <ram:TypeCode>VAT</ram:TypeCode>
            <ram:CategoryCode>${invoice.vatEnabled ? 'S' : 'E'}</ram:CategoryCode>
            <ram:RateApplicablePercent>${Number(invoice.vatRate) || 0}</ram:RateApplicablePercent>
          </ram:ApplicableTradeTax>
          <ram:SpecifiedTradeSettlementLineMonetarySummation>
            <ram:LineTotalAmount>${lineAmount.toFixed(2)}</ram:LineTotalAmount>
          </ram:SpecifiedTradeSettlementLineMonetarySummation>
        </ram:SpecifiedLineTradeSettlement>
      </ram:IncludedSupplyChainTradeLineItem>`;
  });

  return `<?xml version="1.0" encoding="UTF-8"?>
<rsm:CrossIndustryInvoice xmlns:rsm="urn:un:unece:uncefact:data:standard:CrossIndustryInvoice:100"
  xmlns:qdt="urn:un:unece:uncefact:data:standard:QualifiedDataType:100"
  xmlns:ram="urn:un:unece:uncefact:data:standard:ReusableAggregateBusinessInformationEntity:100"
  xmlns:udt="urn:un:unece:uncefact:data:standard:UnqualifiedDataType:100">
  <rsm:ExchangedDocumentContext>
    <ram:GuidelineSpecifiedDocumentContextParameter>
      <ram:ID>urn:factur-x.eu:1p0:basic</ram:ID>
    </ram:GuidelineSpecifiedDocumentContextParameter>
  </rsm:ExchangedDocumentContext>
  <rsm:ExchangedDocument>
    <ram:ID>${escapeXml(invoice.number)}</ram:ID>
    <ram:TypeCode>380</ram:TypeCode>
    <ram:IssueDateTime>
      <udt:DateTimeString format="102">${issueDate}</udt:DateTimeString>
    </ram:IssueDateTime>
  </rsm:ExchangedDocument>
  <rsm:SupplyChainTradeTransaction>
    ${lines.join('')}
    <ram:ApplicableHeaderTradeAgreement>
      <ram:SellerTradeParty>
        <ram:Name>${escapeXml(invoice.company)}</ram:Name>
      </ram:SellerTradeParty>
      <ram:BuyerTradeParty>
        <ram:Name>${escapeXml(invoice.client)}</ram:Name>
      </ram:BuyerTradeParty>
    </ram:ApplicableHeaderTradeAgreement>
    <ram:ApplicableHeaderTradeDelivery>
      <ram:ActualDeliverySupplyChainEvent>
        <ram:OccurrenceDateTime>
          <udt:DateTimeString format="102">${serviceDate}</udt:DateTimeString>
        </ram:OccurrenceDateTime>
      </ram:ActualDeliverySupplyChainEvent>
    </ram:ApplicableHeaderTradeDelivery>
    <ram:ApplicableHeaderTradeSettlement>
      <ram:InvoiceCurrencyCode>${escapeXml(invoice.currency || 'EUR')}</ram:InvoiceCurrencyCode>
      <ram:SpecifiedTradeSettlementPaymentMeans>
        <ram:TypeCode>58</ram:TypeCode>
        <ram:Information>${escapeXml(invoice.paymentLabel || '')}</ram:Information>
        <ram:PayeePartyCreditorFinancialAccount>
          <ram:IBANID>${escapeXml(invoice.iban || '')}</ram:IBANID>
        </ram:PayeePartyCreditorFinancialAccount>
      </ram:SpecifiedTradeSettlementPaymentMeans>
      <ram:ApplicableTradeTax>
        <ram:CalculatedAmount>${Number(invoice.vatAmount || 0).toFixed(2)}</ram:CalculatedAmount>
        <ram:TypeCode>VAT</ram:TypeCode>
        <ram:BasisAmount>${Number(invoice.amount || 0).toFixed(2)}</ram:BasisAmount>
        <ram:CategoryCode>${invoice.vatEnabled ? 'S' : 'E'}</ram:CategoryCode>
        <ram:RateApplicablePercent>${Number(invoice.vatRate) || 0}</ram:RateApplicablePercent>
      </ram:ApplicableTradeTax>
      <ram:SpecifiedTradePaymentTerms>
        <ram:Description>${escapeXml(invoice.penalties || '')}</ram:Description>
        <ram:DueDateDateTime>
          <udt:DateTimeString format="102">${dueDate}</udt:DateTimeString>
        </ram:DueDateDateTime>
      </ram:SpecifiedTradePaymentTerms>
      <ram:SpecifiedTradeSettlementHeaderMonetarySummation>
        <ram:LineTotalAmount>${Number(invoice.amount || 0).toFixed(2)}</ram:LineTotalAmount>
        <ram:TaxBasisTotalAmount>${Number(invoice.amount || 0).toFixed(2)}</ram:TaxBasisTotalAmount>
        <ram:TaxTotalAmount currencyID="${escapeXml(invoice.currency || 'EUR')}">${Number(invoice.vatAmount || 0).toFixed(2)}</ram:TaxTotalAmount>
        <ram:GrandTotalAmount>${Number(invoice.total || 0).toFixed(2)}</ram:GrandTotalAmount>
        <ram:DuePayableAmount>${Number(invoice.total || 0).toFixed(2)}</ram:DuePayableAmount>
      </ram:SpecifiedTradeSettlementHeaderMonetarySummation>
    </ram:ApplicableHeaderTradeSettlement>
  </rsm:SupplyChainTradeTransaction>
</rsm:CrossIndustryInvoice>`;
}

export function buildUblXml(invoice) {
  return `<?xml version="1.0" encoding="UTF-8"?>
<Invoice xmlns="urn:oasis:names:specification:ubl:schema:xsd:Invoice-2">
  <ID>${escapeXml(invoice.number)}</ID>
  <IssueDate>${escapeXml(invoice.issueDate)}</IssueDate>
  <DueDate>${escapeXml(invoice.dueDate)}</DueDate>
  <DocumentCurrencyCode>${escapeXml(invoice.currency || 'EUR')}</DocumentCurrencyCode>
</Invoice>`;
}

export async function buildInvoicePdfBytes(invoice) {
  const pdf = await PDFDocument.create();
  const page = pdf.addPage([595, 842]);
  const regular = await pdf.embedFont(StandardFonts.Helvetica);
  const bold = await pdf.embedFont(StandardFonts.HelveticaBold);
  const accent = hexToRgb(invoice.accent);
  const dark = rgb(0.06, 0.16, 0.26);

  page.drawRectangle({ x: 0, y: 798, width: 595, height: 44, color: accent });
  page.drawRectangle({ x: 42, y: 720, width: 511, height: 1, color: rgb(0.9, 0.93, 0.95) });

  const logo = await embedLogo(pdf, invoice.logo);
  if (logo) {
    const dims = logo.scaleToFit(96, 48);
    page.drawImage(logo, { x: 42, y: 736, width: dims.width, height: dims.height });
  }

  page.drawText('FACTURE', { x: 42, y: 690, size: 26, font: bold, color: dark });
  page.drawText(invoice.number, { x: 42, y: 668, size: 11, font: regular, color: rgb(0.4, 0.49, 0.6) });

  drawLabel(page, 'Emetteur', 42, 628, bold);
  page.drawText(invoice.company, { x: 42, y: 610, size: 12, font: bold, color: dark });
  drawWrappedText(page, invoice.address, 42, 594, 220, regular, 10, dark, 12);
  page.drawText(`SIRET: ${invoice.siret}`, { x: 42, y: 560, size: 10, font: regular, color: dark });
  drawWrappedText(page, `TVA: ${invoice.vatNumber}`, 42, 544, 220, regular, 10, dark, 12);

  drawLabel(page, 'Client', 320, 628, bold);
  page.drawText(invoice.client, { x: 320, y: 610, size: 12, font: bold, color: dark });
  drawWrappedText(page, invoice.clientAddress, 320, 594, 190, regular, 10, dark, 12);

  drawLabel(page, 'Date d emission', 42, 500, bold);
  page.drawText(invoice.issueDate, { x: 42, y: 484, size: 10, font: regular, color: dark });
  drawLabel(page, 'Date de prestation', 185, 500, bold);
  page.drawText(invoice.serviceDate, { x: 185, y: 484, size: 10, font: regular, color: dark });
  drawLabel(page, 'Echeance', 350, 500, bold);
  page.drawText(invoice.dueDate, { x: 350, y: 484, size: 10, font: regular, color: dark });

  page.drawRectangle({ x: 42, y: 434, width: 511, height: 30, color: rgb(0.96, 0.98, 0.99) });
  drawLabel(page, 'Prestation', 52, 445, bold);
  drawLabel(page, 'Qté', 290, 445, bold);
  drawLabel(page, 'Unite', 340, 445, bold);
  drawLabel(page, 'PU HT', 404, 445, bold);
  drawLabel(page, 'Montant HT', 476, 445, bold);

  let y = 414;
  (invoice.lines || []).slice(0, 8).forEach((line) => {
    drawWrappedText(page, line.description, 52, y, 220, regular, 10.5, dark, 12);
    page.drawText(String(line.quantity), { x: 290, y, size: 10.5, font: regular, color: dark });
    page.drawText(String(line.unit || '-'), { x: 340, y, size: 10.5, font: regular, color: dark });
    page.drawText(money(line.unitAmount, invoice.currency), { x: 404, y, size: 10.5, font: regular, color: dark });
    page.drawText(money(line.lineAmount, invoice.currency), { x: 476, y, size: 10.5, font: regular, color: dark });
    y -= 24;
  });

  page.drawRectangle({ x: 330, y: 286, width: 223, height: 106, color: rgb(0.98, 0.99, 1) });
  drawLabel(page, 'Sous-total HT', 346, 364, bold);
  page.drawText(money(invoice.amount, invoice.currency), { x: 450, y: 364, size: 10.5, font: regular, color: dark });
  drawLabel(page, invoice.vatEnabled ? `TVA ${Number(invoice.vatRate || 0)}%` : 'TVA', 346, 338, bold);
  page.drawText(money(invoice.vatAmount, invoice.currency), { x: 450, y: 338, size: 10.5, font: regular, color: dark });
  page.drawRectangle({ x: 346, y: 298, width: 191, height: 28, color: accent });
  page.drawText('Total TTC', { x: 358, y: 308, size: 11, font: bold, color: rgb(1, 1, 1) });
  page.drawText(money(invoice.total, invoice.currency), { x: 450, y: 308, size: 11, font: bold, color: rgb(1, 1, 1) });

  drawLabel(page, 'Paiement', 42, 250, bold);
  drawWrappedText(page, `${invoice.paymentLabel} - ${invoice.iban}`, 42, 234, 250, regular, 10, dark, 12);

  drawLabel(page, 'Mentions', 42, 188, bold);
  let legalY = drawWrappedText(page, invoice.vatLegal, 42, 172, 511, regular, 9, dark, 11);
  legalY = drawWrappedText(page, invoice.penalties, 42, legalY - 16, 511, regular, 9, dark, 11);
  legalY = drawWrappedText(page, invoice.indemnity, 42, legalY - 16, 511, regular, 9, dark, 11);
  drawWrappedText(page, invoice.notes || '-', 42, legalY - 20, 511, regular, 10, dark, 12);

  return pdf.save();
}

export async function buildFacturXPdfBytes(invoice) {
  const pdf = await PDFDocument.load(await buildInvoicePdfBytes(invoice));
  const xml = buildCiiXml(invoice);
  const xmlBytes = new TextEncoder().encode(xml);

  if (typeof pdf.attach === 'function') {
    await pdf.attach(xmlBytes, 'factur-x.xml', {
      mimeType: 'application/xml',
      description: 'Factur-X XML invoice',
      creationDate: new Date(),
      modificationDate: new Date(),
    });
  }

  return pdf.save();
}
